<script setup>
/**
 * 对话（AO.8.3）：主人和自己名下的 Agent 说话、看它一边干一边显示（文字逐字出现、命令卡片、思考、改的文件）。
 * 左栏是我的 Agent 和在线状态，右栏是和选中的那个的对话。数据经平台中转（docs-html/done/agent-chat.html）：
 * 历史用 GET …/chat 按 seq 翻页，实时用 …/chat/stream；断线后退避重连，并从历史补上断开期间存下的事件。
 * 布局参照 DeepSeek Harness 的 ChatView / AssistantNodeView / CommandNodeView / ReasoningRow，用 Vue 重写。
 */
import MarkdownIt from 'markdown-it'
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, EmptyState } from '@drama/ui'

import { api, getToken, readStream, session, upload } from '../api.js'
import { applyEvent, chatItems, emptyChat } from '../chat.js'

const props = defineProps({ embedded: { type: Boolean, default: false } })
const emit = defineEmits(['reply', 'dictation'])
const notified = new Set()
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
// Agent 写的文字是别人的内容：不认 HTML（原样转义），链接只放行 http(s) / mailto（markdown-it 默认拒绝 javascript: 等）。
const md = new MarkdownIt({ html: false, linkify: true, breaks: true })

const PAGE = 50
const agents = computed(() => session.agents.filter((agent) => agent.status !== 'deleted'))
const selectedId = computed(() => (typeof route.params.id === 'string' ? route.params.id : null))
const selected = computed(() => agents.value.find((agent) => agent.id === selectedId.value) ?? null)
/** 左栏每个 Agent 的在线 / 干活状态：打开页面时各取一次，选中的那个随实时流更新。 */
const status = reactive({})

const chat = reactive(emptyChat())
const items = computed(() => chatItems(chat))
/** 这个 Agent 上传命令输出和文件改动吗（主人可以关掉，关掉后只传文字回复）；还没取回来时为 null。 */
const shareCommands = ref(null)
const hasOlder = ref(false)
const loadingOlder = ref(false)
const draft = ref('')
const recording = ref(false)
const dictationState = ref('idle')
const preparingDictation = ref(false)
const recordedSeconds = ref(0)
const transcribing = ref(false)
const dictationLabel = computed(() => recording.value ? 'agentIsland.dictationStop' : transcribing.value ? 'agentIsland.dictationTranscribing' : preparingDictation.value ? 'agentIsland.dictationPermission' : 'agentIsland.dictationStart')
const dictationHint = computed(() => recording.value ? t('agentIsland.dictationRecording', { seconds: recordedSeconds.value }) : transcribing.value ? t('agentIsland.dictationTranscribing') : t('agentIsland.dictationServerHint'))
let capture = null
let dictationGeneration = 0
defineExpose({ abortDictation, scrollToBottom })
const error = ref('')
const connectionError = ref('')
/** 展开的命令卡、思考、文件改动（按条目 key）。 */
const opened = ref(new Set())
const scroller = ref(null)
let controller = null

if (getToken() === null) router.push({ path: '/login', query: { next: route.fullPath } })

// 没选 Agent 时选第一个（宽屏）；手机宽度下先显示列表，由人点选。
watch(agents, (list) => {
  if (!props.embedded) for (const agent of list) if (status[agent.id] === undefined) loadStatus(agent.id)
  if (!props.embedded && selectedId.value === null && list.length > 0 && window.matchMedia('(min-width: 769px)').matches) router.replace(`/chat/${list[0].id}`)
}, { immediate: true })

/** @param {string} id */
async function loadStatus(id) {
  status[id] = { online: false, working: false }
  const page = await api('GET', `/api/voko/agents/${id}/chat`)
  status[id] = { online: page.online, working: page.working }
}

watch(selectedId, (id) => {
  abortDictation()
  if (controller !== null) controller.abort()
  notified.clear()
  Object.assign(chat, emptyChat())
  shareCommands.value = null
  hasOlder.value = false
  error.value = ''
  connectionError.value = ''
  draft.value = ''
  if (id !== null) connect(id)
}, { immediate: true })

onBeforeUnmount(() => {
  abortDictation()
  if (controller !== null) controller.abort()
})

/**
 * 打开实时流，并从历史补齐；流断了按 1、2、4…30 秒退避重连，每次连上都再补一次历史。
 * @param {string} id
 */
async function connect(id) {
  const own = new AbortController()
  controller = own
  let wait = 1000
  while (!own.signal.aborted) {
    const since = Date.now()
    const attempt = new AbortController()
    const signal = AbortSignal.any([own.signal, attempt.signal])
    try {
      // Attach both rejection handlers immediately: either request can fail first during a restart.
      await Promise.all([fillHistory(id), readStream(`/api/voko/agents/${id}/chat/stream`, onEvent, signal, () => { connectionError.value = '' })])
    } catch (err) {
      connectionError.value = requestError(err)
      // 不是自己的 Agent、Agent 进了回收站：不再重连。
      if (err.code === 'NOT_FOUND' || err.code === 'CONFLICT') return
    } finally {
      attempt.abort()
    }
    if (own.signal.aborted) return
    status[id] = { ...status[id], online: false }
    chat.online = false
    // 连上过一阵子再断的，从 1 秒重新算。
    wait = Date.now() - since > 30_000 ? 1000 : Math.min(wait * 2, 30_000)
    await new Promise((resolve) => setTimeout(resolve, wait))
  }
}

/** @param {any} event */
function onEvent(event) {
  connectionError.value = ''
  const stick = nearBottom()
  applyEvent(chat, event)
  // Only new complete replies are spoken; history loading never emits and reconnects cannot replay a seq.
  if (props.embedded && event.type === 'agent_message' && event.payload.phase !== 'commentary' && !notified.has(event.seq)) {
    notified.add(event.seq)
    emit('reply', { agent_id: selectedId.value, text: event.payload.text })
  }
  status[selectedId.value] = { online: chat.online, working: chat.working }
  if (stick) scrollToBottom()
}

/**
 * 取最新一页历史；和已有的接不上（断开期间存下的超过一页）时往前多翻几页。
 * @param {string} id
 */
async function fillHistory(id) {
  const known = chat.saved.size === 0 ? 0 : Math.max(...chat.saved.keys())
  let page = await api('GET', `/api/voko/agents/${id}/chat`)
  chat.online = page.online
  chat.working = page.working
  shareCommands.value = page.share_commands
  status[id] = { online: page.online, working: page.working }
  const first = chat.saved.size === 0
  for (;;) {
    page.events.forEach((event) => applyEvent(chat, event))
    const oldest = page.events.length === 0 ? null : page.events[0].seq
    if (first) hasOlder.value = page.events.length === PAGE
    if (first || oldest === null || oldest <= known + 1 || page.events.length < PAGE) break
    page = await api('GET', `/api/voko/agents/${id}/chat?before_seq=${oldest}`)
  }
  if (first) scrollToBottom()
}

/** 滚到顶时取更早的一页，并保持视线停在原来那条上。 */
async function loadOlder() {
  if (!hasOlder.value || loadingOlder.value || chat.saved.size === 0) return
  loadingOlder.value = true
  const box = scroller.value
  const before = box.scrollHeight
  try {
    const page = await api('GET', `/api/voko/agents/${selectedId.value}/chat?before_seq=${Math.min(...chat.saved.keys())}`)
    page.events.forEach((event) => applyEvent(chat, event))
    hasOlder.value = page.events.length === PAGE
    await nextTick()
    box.scrollTop += box.scrollHeight - before
  } finally {
    loadingOlder.value = false
  }
}

function onScroll() {
  if (scroller.value.scrollTop < 80) loadOlder()
}

function nearBottom() {
  const box = scroller.value
  return box === null || box.scrollHeight - box.scrollTop - box.clientHeight < 120
}

async function scrollToBottom() {
  await nextTick()
  if (scroller.value !== null) scroller.value.scrollTop = scroller.value.scrollHeight
}

function requestError(err) {
  return err.code === 'INVALID_HTTP_RESPONSE' ? t('agentIsland.httpError', { status: err.status }) : err.message
}

/** Native recording replaces the browser's unavailable recognition service. */
async function toggleDictation() {
  if (preparingDictation.value || transcribing.value) return
  if (capture !== null) {
    if (capture.recorder.state === 'recording') capture.recorder.stop()
    return
  }
  error.value = ''
  if (!navigator.mediaDevices?.getUserMedia || !('MediaRecorder' in window)) {
    error.value = t('agentIsland.dictationUnavailable')
    return
  }
  const mime = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'].find(type => MediaRecorder.isTypeSupported(type))
  if (mime === undefined) {
    error.value = t('agentIsland.dictationUnavailable')
    return
  }
  const generation = ++dictationGeneration
  const id = selectedId.value
  const language = locale.value
  const before = draft.value
  preparingDictation.value = true
  dictationState.value = 'permission'
  const permissionTimer = setTimeout(() => {
    if (generation !== dictationGeneration) return
    abortDictation()
    error.value = t('agentIsland.dictationPermissionTimeout')
  }, 15_000)
  let stream
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
  } catch (err) {
    if (generation === dictationGeneration) {
      dictationState.value = 'failed'
      error.value = t('agentIsland.dictationError', { code: err.name })
    }
    return
  } finally {
    clearTimeout(permissionTimer)
    if (generation === dictationGeneration) preparingDictation.value = false
  }
  if (generation !== dictationGeneration) {
    stream.getTracks().forEach(track => track.stop())
    return
  }
  try {
    const recorder = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 64000 })
    const own = { recorder, stream, chunks: [], timer: null, deadline: null, controller: new AbortController() }
    capture = own
    recorder.ondataavailable = event => { if (event.data.size > 0) own.chunks.push(event.data) }
    recorder.onerror = () => {
      abortDictation()
      error.value = t('agentIsland.dictationError', { code: 'recorder' })
    }
    recorder.onstop = async () => {
      clearInterval(own.timer)
      clearTimeout(own.deadline)
      stream.getTracks().forEach(track => track.stop())
      if (generation !== dictationGeneration) return
      recording.value = false
      emit('dictation', false)
      transcribing.value = true
      dictationState.value = 'transcribing'
      try {
        const audio = new File(own.chunks, 'speech', { type: mime })
        const result = await upload(`/api/voko/agents/${id}/chat/transcribe?language=${language}`, audio, AbortSignal.any([own.controller.signal, AbortSignal.timeout(55_000)]))
        if (generation !== dictationGeneration) return
        draft.value = before + result.text
        dictationState.value = 'ready'
      } catch (err) {
        if (generation !== dictationGeneration) return
        dictationState.value = 'failed'
        const key = { NO_SPEECH: 'dictationNoSpeech', AUDIO_TOO_LONG: 'dictationTooLong', INVALID_AUDIO: 'dictationInvalidAudio', SPEECH_BUSY: 'dictationBusy', RATE_LIMITED: 'dictationBusy' }[err.code]
        error.value = t(`agentIsland.${key ?? 'dictationServerError'}`)
      } finally {
        if (generation === dictationGeneration) {
          capture = null
          transcribing.value = false
        }
      }
    }
    recorder.start(1000)
    recording.value = true
    recordedSeconds.value = 0
    dictationState.value = 'recording'
    emit('dictation', true)
    own.timer = setInterval(() => { recordedSeconds.value++ }, 1000)
    // Leave one second for recorder/container overhead below the server's 30-second limit.
    own.deadline = setTimeout(() => recorder.stop(), 29_000)
  } catch (err) {
    stream.getTracks().forEach(track => track.stop())
    abortDictation()
    error.value = t('agentIsland.dictationError', { code: err.name })
  }
}
function abortDictation() {
  dictationGeneration++
  preparingDictation.value = false
  transcribing.value = false
  if (capture !== null) {
    const own = capture
    capture = null
    clearInterval(own.timer)
    clearTimeout(own.deadline)
    own.controller.abort()
    own.recorder.onstop = null
    own.recorder.ondataavailable = null
    own.recorder.onerror = null
    if (own.recorder.state !== 'inactive') own.recorder.stop()
    own.stream.getTracks().forEach(track => track.stop())
  }
  dictationState.value = 'aborted'
  recording.value = false
  emit('dictation', false)
}

async function send() {
  if (recording.value || transcribing.value || preparingDictation.value) return
  const text = draft.value.trim()
  if (text === '') return
  error.value = ''
  try {
    await api('POST', `/api/voko/agents/${selectedId.value}/chat/messages`, { text })
    draft.value = ''
    scrollToBottom()
  } catch (err) {
    error.value = requestError(err)
  }
}

/** Enter 发送，Shift+Enter 换行；输入法组字时的 Enter 不算。 @param {KeyboardEvent} event */
function onKey(event) {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
  event.preventDefault()
  send()
}

/** 开关「上传命令与文件改动」：页面先变，失败了改回去并提示。 */
async function setShare(value) {
  const before = shareCommands.value
  shareCommands.value = value
  error.value = ''
  try {
    shareCommands.value = (await api('PUT', `/api/voko/agents/${selectedId.value}/chat/settings`, { share_commands: value })).share_commands
  } catch (err) {
    shareCommands.value = before
    error.value = requestError(err)
  }
}

async function stop() {
  error.value = ''
  try {
    await api('POST', `/api/voko/agents/${selectedId.value}/chat/stop`, {})
  } catch (err) {
    error.value = requestError(err)
  }
}

/** @param {string} key */
function toggle(key) {
  const next = new Set(opened.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  opened.value = next
}

/** @param {{ online: boolean, working: boolean } | undefined} value */
const stateOf = (value) => (value === undefined || !value.online ? 'offline' : value.working ? 'working' : 'online')
const seconds = (ms) => (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`)
const time = (iso) => new Date(iso).toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' })
</script>

<template>
  <div class="chat" :class="{ 'has-selection': selectedId !== null, 'is-embedded': embedded }">
    <aside v-if="!embedded" class="chat-list" :aria-label="t('chat.agents')">
      <h1 class="chat-list-title">{{ t('chat.title') }}</h1>
      <EmptyState v-if="agents.length === 0" :title="t('agents.empty')">
        <template #action>
          <Button @click="router.push('/agents/new')">{{ t('shell.newAgent') }}</Button>
        </template>
      </EmptyState>
      <router-link v-for="agent in agents" :key="agent.id" :to="`/chat/${agent.id}`" class="chat-agent" :class="{ 'is-current': agent.id === selectedId }">
        <span class="chat-dot" :class="`is-${stateOf(status[agent.id])}`" aria-hidden="true" />
        <span class="chat-agent-name">{{ agent.full_name }}</span>
        <span class="chat-agent-state">{{ t(`chat.state.${stateOf(status[agent.id])}`) }}</span>
      </router-link>
    </aside>

    <section v-if="selected" class="chat-main" :aria-label="t('chat.with', { name: selected.full_name })">
      <header class="chat-head">
        <router-link v-if="!embedded" to="/chat" class="chat-back" :aria-label="t('chat.agents')">‹</router-link>
        <strong>{{ selected.full_name }}</strong>
        <span class="chat-dot" :class="`is-${stateOf({ online: chat.online, working: chat.working })}`" aria-hidden="true" />
        <span class="voko-muted">{{ t(`chat.state.${stateOf({ online: chat.online, working: chat.working })}`) }}</span>
        <router-link v-if="!embedded" :to="`/agent-island/${selectedId}`">{{ t('agentIsland.enter') }}</router-link>
        <slot name="toolbar" />
        <label v-if="!embedded && shareCommands !== null" class="chat-share" :title="t('chat.shareHint')">
          <input type="checkbox" :checked="shareCommands" :aria-label="t('chat.share')" @change="setShare($event.target.checked)">
          {{ t('chat.share') }}
        </label>
      </header>

      <div ref="scroller" class="chat-scroll" role="log" aria-live="polite" @scroll="onScroll">
        <p v-if="hasOlder" class="chat-more voko-muted">{{ loadingOlder ? t('chat.loading') : t('chat.older') }}</p>
        <p v-if="items.length === 0" class="chat-empty voko-muted">{{ t('chat.empty', { name: selected.full_name }) }}</p>
        <template v-for="item in items" :key="item.key">
          <div v-if="item.kind === 'owner'" class="chat-owner">
            <div class="chat-bubble">{{ item.text }}</div>
            <span v-if="item.at !== null" class="chat-time">{{ time(item.at) }}</span>
          </div>
          <p v-else-if="item.kind === 'stop'" class="chat-notice">{{ t('chat.stopped') }}</p>
          <div v-else-if="item.kind === 'turn'" class="chat-turn" role="separator">
            <span>{{ t(`chat.turn.${item.source}`) }}<template v-if="item.status !== null && item.status !== 'completed'"> · {{ t('chat.turnStatus', { status: item.status }) }}</template></span>
          </div>
          <!-- eslint-disable-next-line vue/no-v-html -- markdown-it 关闭了 HTML，输出只有它自己生成的标签 -->
          <div v-else-if="item.kind === 'agent'" class="chat-agent-text" :class="{ 'is-streaming': item.streaming, 'is-commentary': item.phase === 'commentary' }" v-html="md.render(item.text)" />
          <div v-else-if="item.kind === 'reasoning'" class="chat-row" :class="{ 'is-running': item.streaming }">
            <button type="button" class="chat-row-head" :aria-expanded="opened.has(item.key)" @click="toggle(item.key)">
              <span class="chat-chevron" aria-hidden="true">{{ opened.has(item.key) ? '▾' : '▸' }}</span>
              <span class="chat-row-title">{{ t('chat.reasoning') }}</span>
            </button>
            <pre v-if="opened.has(item.key)" class="chat-body is-prose">{{ item.text }}</pre>
          </div>
          <div v-else-if="item.kind === 'command'" class="chat-row" :class="{ 'is-running': item.running }">
            <button type="button" class="chat-row-head" :aria-expanded="opened.has(item.key)" @click="toggle(item.key)">
              <span class="chat-chevron" aria-hidden="true">{{ opened.has(item.key) ? '▾' : '▸' }}</span>
              <span class="chat-row-title">{{ item.running ? t('chat.running') : t('chat.ran') }}</span>
              <code class="chat-command">{{ item.command }}</code>
              <span v-if="item.exit_code !== null" class="chat-exit" :class="item.exit_code === 0 ? 'is-ok' : 'is-fail'">{{ t('chat.exit', { code: item.exit_code }) }}</span>
              <span v-if="item.duration_ms !== null" class="chat-time">{{ seconds(item.duration_ms) }}</span>
            </button>
            <div v-if="opened.has(item.key)">
              <p v-if="item.cwd !== null" class="chat-cwd">{{ item.cwd }}</p>
              <pre class="chat-body" :class="{ 'is-fail': item.exit_code !== null && item.exit_code !== 0 }">{{ item.output === '' ? t('chat.noOutput') : item.output }}</pre>
              <p v-if="item.truncated" class="chat-time">{{ t('chat.truncated') }}</p>
            </div>
          </div>
          <div v-else-if="item.kind === 'files'" class="chat-row">
            <button type="button" class="chat-row-head" :aria-expanded="opened.has(item.key)" @click="toggle(item.key)">
              <span class="chat-chevron" aria-hidden="true">{{ opened.has(item.key) ? '▾' : '▸' }}</span>
              <span class="chat-row-title">{{ t('chat.files', { n: item.changes.length }) }}</span>
              <code class="chat-command">{{ item.changes.map((change) => change.path).join(', ') }}</code>
            </button>
            <div v-if="opened.has(item.key)">
              <template v-for="change in item.changes" :key="change.path">
                <p class="chat-cwd">{{ change.kind }} · {{ change.path }}</p>
                <pre class="chat-body">{{ change.diff }}</pre>
              </template>
            </div>
          </div>
        </template>
      </div>

      <p v-if="connectionError" class="chat-error" role="alert">{{ connectionError }}</p>
      <p v-if="error" class="chat-error" role="alert">{{ error }}</p>
      <form class="chat-input" @submit.prevent="send">
        <textarea v-model="draft" :rows="embedded ? 1 : 2" :disabled="recording || transcribing || preparingDictation" :placeholder="embedded ? t('agentIsland.message') : t('chat.placeholder', { name: selected.full_name })" :aria-label="t('chat.placeholder', { name: selected.full_name })" @keydown="onKey" />
        <div class="chat-actions">
          <Button v-if="embedded" type="button" size="sm" variant="ghost" :disabled="preparingDictation || transcribing" :aria-pressed="recording" :data-dictation-state="dictationState" data-dictation-provider="self-hosted" @click="toggleDictation">{{ t(dictationLabel) }}</Button>
          <Button v-if="chat.working" type="button" variant="ghost" @click="stop">{{ t('chat.stop') }}</Button>
          <Button type="submit" :disabled="recording || transcribing || preparingDictation || draft.trim() === ''">{{ t('chat.send') }}</Button>
        </div>
        <p v-if="embedded" class="chat-hint voko-muted" role="status">{{ dictationHint }}</p>
        <p v-else class="chat-hint voko-muted">{{ chat.online ? t('chat.hintOnline') : t('chat.hintOffline') }}</p>
      </form>
    </section>
    <section v-else class="chat-main chat-placeholder">
      <p class="voko-muted">{{ t('chat.pick') }}</p>
    </section>
  </div>
</template>

<style scoped>
.chat {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: var(--drama-space-lg);
  height: calc(100vh - 60px - 2 * var(--drama-space-xl));
  min-height: 420px;
}
.chat-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
}
.chat-list-title {
  margin: 0 0 var(--drama-space-md);
  font-size: var(--drama-font-size-xl);
}
.chat-agent {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-sm) var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-text);
}
.chat-agent:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
  color: var(--drama-color-text);
}
.chat-agent.is-current {
  background: color-mix(in srgb, var(--drama-color-primary) 14%, transparent);
}
.chat-agent-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat-agent-state {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}
.chat-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-border-strong);
}
.chat-dot.is-online {
  background: var(--drama-color-success);
}
.chat-dot.is-working {
  background: var(--drama-color-warning);
}

.chat-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface);
}
.chat-placeholder {
  align-items: center;
  justify-content: center;
}
.chat-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-md) var(--drama-space-lg);
  border-bottom: 1px solid var(--drama-color-border);
}
.chat-share {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  margin-left: auto;
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text-muted);
  cursor: pointer;
}
.chat-back {
  display: none;
  font-size: var(--drama-font-size-xl);
  line-height: 1;
  color: var(--drama-color-text);
}
.chat-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  padding: var(--drama-space-lg);
}
/* 对话超过一屏时，竖向 flex 会压缩条目；设了 overflow:hidden 的命令卡会被压成 0 高度。条目一律不压缩，由外面滚动。 */
.chat-scroll > * {
  flex-shrink: 0;
}
.chat-more,
.chat-empty {
  align-self: center;
  margin: 0;
}

/* 主人的消息：靠右的圆角气泡。 */
.chat-owner {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.chat-bubble {
  max-width: min(525px, 82%);
  padding: 10px 16px;
  border-radius: 22px;
  background: color-mix(in srgb, var(--drama-color-primary) 14%, var(--drama-color-surface-alt));
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.chat-time {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}
.chat-notice {
  align-self: center;
  margin: 0;
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text-muted);
}
.chat-turn {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}
.chat-turn::before,
.chat-turn::after {
  content: '';
  flex: 1;
  border-top: 1px solid var(--drama-color-border);
}

/* Agent 的回复：没有气泡的 markdown；还在打字时末尾有光标。 */
.chat-agent-text {
  max-width: 100%;
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.chat-agent-text.is-commentary {
  color: var(--drama-color-text-muted);
}
.chat-agent-text :deep(p) {
  margin: 0 0 var(--drama-space-sm);
}
.chat-agent-text :deep(p:last-child) {
  margin-bottom: 0;
}
.chat-agent-text :deep(pre) {
  overflow-x: auto;
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
}
.chat-agent-text :deep(code) {
  font-family: var(--drama-font-family-mono);
  font-size: 0.92em;
}
.chat-agent-text.is-streaming > :deep(:last-child)::after {
  content: '▍';
  margin-left: 2px;
  animation: chat-caret 1s steps(1) infinite;
}
@keyframes chat-caret {
  50% { opacity: 0; }
}

/* 命令、思考、文件改动：一行标题，点开看内容；进行中有一道扫过的光。 */
.chat-row {
  position: relative;
  overflow: hidden;
}
.chat-row.is-running .chat-row-head::after {
  content: '';
  position: absolute;
  inset-block: 0;
  left: -300px;
  width: 300px;
  background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--drama-color-surface) 60%, transparent), transparent);
  animation: chat-sweep 2.6s ease-out infinite;
  pointer-events: none;
}
@keyframes chat-sweep {
  0% { left: -300px; }
  90%, 100% { left: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .chat-row.is-running .chat-row-head::after,
  .chat-agent-text.is-streaming > :deep(:last-child)::after {
    animation: none;
  }
}
.chat-row-head {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  width: 100%;
  min-width: 0;
  padding: 2px 0;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.chat-chevron {
  color: var(--drama-color-text-muted);
}
.chat-row-title {
  flex-shrink: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
.chat-command {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
}
.chat-exit {
  flex-shrink: 0;
  font-size: var(--drama-font-size-xs);
}
.chat-exit.is-ok {
  color: var(--drama-color-success);
}
.chat-exit.is-fail {
  color: var(--drama-color-danger);
}
.chat-cwd {
  margin: var(--drama-space-xs) 0 0 var(--drama-space-lg);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}
.chat-body {
  max-height: 260px;
  margin: var(--drama-space-xs) 0 var(--drama-space-xs) var(--drama-space-lg);
  padding: 12px 16px;
  overflow: auto;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.chat-body.is-prose {
  font-family: var(--drama-font-family-body);
}
.chat-body.is-fail {
  color: var(--drama-color-danger);
}

.chat-error {
  margin: 0;
  padding: 0 var(--drama-space-lg);
  color: var(--drama-color-danger);
}
.chat-input {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-md) var(--drama-space-lg);
  border-top: 1px solid var(--drama-color-border);
}
.chat-input textarea {
  resize: none;
  padding: var(--drama-space-sm) var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
}
.chat-input textarea:focus {
  outline: none;
  border-color: var(--drama-color-primary);
}
.chat-actions {
  display: flex;
  align-items: flex-end;
  gap: var(--drama-space-sm);
}
.chat-hint {
  grid-column: 1 / -1;
  margin: 0;
  font-size: var(--drama-font-size-xs);
}

/* 手机宽度：先是 Agent 列表，选中后整屏是对话，左上角可以回到列表。 */
@media (max-width: 768px) {
  .chat {
    grid-template-columns: minmax(0, 1fr);
    height: calc(100vh - 60px - 2 * var(--drama-space-md));
  }
  .chat.has-selection .chat-list,
  .chat:not(.has-selection) .chat-main {
    display: none;
  }
  .chat-back {
    display: inline;
  }
}
.chat.is-embedded { display: block; height: clamp(190px, 30dvh, 260px); min-height: 0; }
.chat.is-embedded .chat-main { height: 100%; }
.chat.is-embedded .chat-head { padding: var(--drama-space-xs) var(--drama-space-sm); font-size: var(--drama-font-size-sm); flex-wrap: nowrap; }
.chat.is-embedded .chat-head :deep(button) { margin-left: auto; white-space: nowrap; }
.chat.is-embedded .chat-scroll { padding: var(--drama-space-sm); gap: var(--drama-space-sm); font-size: var(--drama-font-size-sm); }
.chat.is-embedded .chat-input { padding: var(--drama-space-sm); }
.chat.is-embedded .chat-actions { flex-wrap: wrap; align-items: center; }
.chat.is-embedded .chat-bubble { padding: var(--drama-space-xs) var(--drama-space-sm); }
</style>
